import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const useAddress = (userId) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch addresses for a specific user
  const fetchAddresses = useCallback(async () => {
    if (!userId) {
      setAddresses([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get("https://inaaniserver-50c4305cc7f7.herokuapp.com/address/getall-by-user", {
        params: { user_id: userId },
      });
      console.log('fetchAddresses response:', response.data); // Debug
      if (response.data && Array.isArray(response.data.data)) {
        const structuredAddresses = response.data.data.map(addr => ({
          id: addr.id,
          user_id: userId, // Ensure user_id is included
          contact: {
            name: addr.name || "N/A",
            phone: addr.phone_number || "N/A",
          },
          location: {
            municipality: addr.municipality || "N/A",
            barangay: addr.barangay || "N/A",
            home: addr.home_address || "N/A",
          },
        }));
        setAddresses(structuredAddresses);
      } else {
        setAddresses([]);
        setError("No addresses found for this user.");
      }
    } catch (err) {
      console.error('fetchAddresses error:', err);
      setAddresses([]);
      setError(err.response?.data?.message || "Failed to fetch addresses");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Add a new address
  const addAddress = useCallback(async (addressData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await axios.post("https://inaaniserver-50c4305cc7f7.herokuapp.com/address/order-address/add", addressData);
      console.log('addAddress response:', response.data); // Debug
      const newAddress = {
        id: response.data.id || Date.now(),
        user_id: userId,
        contact: {
          name: addressData.name || "N/A",
          phone: addressData.phone_number || "N/A",
        },
        location: {
          municipality: addressData.municipality || "N/A",
          barangay: addressData.barangay || "N/A",
          home: addressData.home_address || "N/A",
        },
      };
      setAddresses((prev) => [...prev, newAddress]);
      setSuccess(true);
      return response.data;
    } catch (err) {
      console.error('addAddress error:', err);
      setError(err.response?.data?.message || "Failed to add address");
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Update an existing address
  const updateAddress = useCallback(async (addressData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await axios.post("https://inaaniserver-50c4305cc7f7.herokuapp.com/address/update", addressData);
      console.log('updateAddress response:', response.data); // Debug
      setAddresses((prev) =>
        prev.map((address) =>
          address.id === addressData.id
            ? {
                ...address,
                user_id: userId,
                contact: {
                  name: addressData.name || "N/A",
                  phone: addressData.phone_number || "N/A",
                },
                location: {
                  municipality: addressData.municipality || "N/A",
                  barangay: addressData.barangay || "N/A",
                  home: addressData.home_address || "N/A",
                },
              }
            : address
        )
      );
      setSuccess(true);
      return response.data;
    } catch (err) {
      console.error('updateAddress error:', err);
      setError(err.response?.data?.message || "Failed to update address");
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Delete an address
  const deleteAddress = useCallback(async (addressId) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await axios.delete("https://inaaniserver-50c4305cc7f7.herokuapp.com/address/delete", {
        params: { id: addressId },
      });
      console.log('deleteAddress response:', response.data); // Debug
      if (response.data.error) {
        throw new Error(response.data.message || "Failed to delete address");
      }
      setAddresses((prev) => prev.filter(address => address.id !== addressId));
      setSuccess(true);
      return response.data;
    } catch (err) {
      console.error('deleteAddress error:', err);
      setError(err.response?.data?.message || "Failed to delete address");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  return {
    addresses,
    loading,
    error,
    success,
    setAddresses,
    addAddress,
    updateAddress,
    deleteAddress,
    fetchAddresses,
  };
};

export default useAddress;